/**
 * CDN URL helpers — aligned with Android MediaConfig + MediaUrlResolver.
 * Direct object URLs only; zone transform rules apply format=auto (no /cdn-cgi/image/ — 404 on subdomains).
 */

const CDN_BASE =
  import.meta.env.VITE_CDN_BASE_URL?.trim() || "https://media.daywia.com";

const HOST_FRAMES = "https://frames.daywia.com";
const HOST_PARALLELS = "https://parallels.daywia.com";
const HOST_DRIFTS = "https://drifts.daywia.com";
const HOST_USERS = "https://users.daywia.com";
const HOST_CHAT = "https://chat.daywia.com";

/** Retired hosts — remapped to active CDN bases when normalizing stored URLs. */
const LEGACY_HOSTS = [
  "posts.daywia.com",
  "cdn.daywia.com",
  "images.daywia.com",
  "shorts.daywia.com",
];

const FRAME_FILE = /(?:full|main|medium|thumb)_(\d+)\./i;

function objectPathFromUrl(url: string): string | null {
  try {
    const path = new URL(url.trim()).pathname.replace(/^\//, "");
    return path || null;
  } catch {
    return null;
  }
}

/** Canonical public URL for an R2 object key (matches Android MediaConfig.objectUrl). */
export function objectUrl(relativePath: string): string {
  const path = relativePath.trim().replace(/^\/+/, "");
  const base = cdnBaseForObjectPath(path);
  return `${base.replace(/\/$/, "")}/${path}`;
}

function cdnBaseForObjectPath(relativePath: string): string {
  if (relativePath.startsWith("frames/")) return HOST_FRAMES;
  if (relativePath.startsWith("parallels/")) return HOST_PARALLELS;
  if (relativePath.startsWith("drifts/") || relativePath.startsWith("posts/video/")) {
    return HOST_DRIFTS;
  }
  if (relativePath.startsWith("posts/")) return CDN_BASE.replace(/\/$/, "");
  if (relativePath.startsWith("users/")) return HOST_USERS;
  if (relativePath.startsWith("chat/")) return HOST_CHAT;
  return CDN_BASE.replace(/\/$/, "");
}

function remapLegacyHost(url: string): string {
  let result = url.trim();
  if (!result) return result;

  for (const host of LEGACY_HOSTS) {
    if (!result.toLowerCase().includes(host)) continue;
    const path = objectPathFromUrl(result);
    if (path) result = objectUrl(path);
  }

  const r2Dev = /https:\/\/pub-[a-z0-9]+\.r2\.dev(\/[^?#]*)?/i;
  result = result.replace(r2Dev, (_, pathPart) => {
    const path = (pathPart ?? "").replace(/^\//, "").split("?")[0];
    return path ? objectUrl(path) : CDN_BASE;
  });

  return result;
}

/** Normalize R2 paths and full URLs to the correct CDN host. */
export function normalizeMediaUrl(raw: string | null | undefined): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "";
  if (
    !trimmed.includes("://") &&
    !trimmed.startsWith("content:") &&
    !trimmed.startsWith("file:")
  ) {
    const path = trimmed.replace(/^\/+/, "");
    if (
      path.startsWith("users/") ||
      path.startsWith("drifts/") ||
      path.startsWith("frames/") ||
      path.startsWith("parallels/") ||
      path.startsWith("posts/") ||
      path.startsWith("chat/")
    ) {
      return remapLegacyHost(objectUrl(path));
    }
  }
  return remapLegacyHost(trimmed);
}

/** Feed/display URL — direct CDN bytes (Image Resizing path returns 404 on our zones). */
export function feedImageUrl(raw: string | null | undefined): string {
  return normalizeMediaUrl(raw);
}

export function stripUrlQuery(url: string): string {
  return url.split("?")[0]?.trim() ?? "";
}

/** Avatars — direct CDN, preserve cache-bust query. */
export function avatarDisplayUrl(
  userId: string,
  denormalized?: string | null,
): string {
  const trimmed = denormalized?.trim() ?? "";
  if (trimmed) {
    const base = stripUrlQuery(normalizeMediaUrl(trimmed));
    const q = trimmed.includes("?") ? trimmed.slice(trimmed.indexOf("?")) : "";
    return base + q;
  }
  return normalizeMediaUrl(`users/${userId.trim()}/profile/avatar.webp`);
}

/** One carousel frame per index — prefer full_ over main/medium/thumb. */
export function coalesceFeedImageUrls(urls: string[]): string[] {
  const normalized = urls.map(normalizeMediaUrl).filter(Boolean);
  if (normalized.length <= 1) return [...new Set(normalized)];

  const byFrame = new Map<number, { tier: number; url: string }>();
  const unindexed: string[] = [];

  for (const url of normalized) {
    const file = url.split("/").pop()?.split("?")[0] ?? "";
    const m = FRAME_FILE.exec(file);
    const index = m ? Number(m[1]) : null;
    if (index != null) {
      const tier =
        file.includes("full_") ? 3 : file.includes("main_") || file.includes("medium_") ? 2 : 1;
      const prev = byFrame.get(index);
      if (!prev || tier > prev.tier) byFrame.set(index, { tier, url });
    } else {
      unindexed.push(url);
    }
  }

  if (byFrame.size > 0) {
    const framed = [...byFrame.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, v]) => v.url);
    return framed.length > 0 && unindexed.length === 0
      ? framed
      : [...framed, ...unindexed.filter((u) => !framed.includes(u))];
  }
  return [...new Set(unindexed)];
}

export interface MediaVariant {
  full?: string;
  medium?: string;
  thumb?: string;
}

export function parseMediaArray(raw: unknown): MediaVariant[] {
  if (!Array.isArray(raw)) return [];
  const out: MediaVariant[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    out.push({
      full: o.full ? normalizeMediaUrl(String(o.full)) : undefined,
      medium: o.medium ? normalizeMediaUrl(String(o.medium)) : undefined,
      thumb: o.thumb ? normalizeMediaUrl(String(o.thumb)) : undefined,
    });
  }
  return out;
}

export function pickPostImage(post: {
  mediaUrls: string[];
  thumbnailUrl: string | null;
  mediaVariants?: MediaVariant[];
}): string {
  const fromMedia = post.mediaVariants?.[0];
  if (fromMedia?.medium) return fromMedia.medium;
  if (fromMedia?.full) return fromMedia.full;
  if (fromMedia?.thumb) return fromMedia.thumb;

  const urls = coalesceFeedImageUrls(post.mediaUrls);
  if (urls[0]) return urls[0];
  if (post.thumbnailUrl) return feedImageUrl(post.thumbnailUrl);
  return "";
}

export function resolveMediaUrlsFromRow(row: Record<string, unknown>): string[] {
  const fromArray = mediaUrlsFromElement(row.media_urls);
  if (fromArray.length > 0) return coalesceFeedImageUrls(fromArray);

  const variants = parseMediaArray(row.media);
  if (variants.length > 0) {
    return variants
      .map((v) => v.full || v.medium || v.thumb || "")
      .filter(Boolean);
  }

  const meta = row.video_metadata as Record<string, unknown> | null | undefined;
  const variantsMeta = meta?.variants as Record<string, unknown> | undefined;
  const basePath = String(variantsMeta?.base_path ?? "").replace(/\/$/, "");
  const mainFile = String(variantsMeta?.main ?? "");
  if (basePath && mainFile) {
    return [normalizeMediaUrl(objectUrl(`${basePath}/${mainFile}`))];
  }

  const thumb = row.thumbnail_url;
  if (thumb != null && String(thumb).trim()) {
    return [normalizeMediaUrl(String(thumb))];
  }
  return [];
}

function mediaUrlsFromElement(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((el) => {
        if (typeof el === "string") return normalizeMediaUrl(el);
        if (el && typeof el === "object" && "url" in el) {
          return normalizeMediaUrl(String((el as { url: unknown }).url));
        }
        return "";
      })
      .filter(Boolean);
  }
  return [];
}
